import { baseApi } from 'services';
import {
  TDisableRightsOwnersPayload,
  TDisableRightsOwnersResponse,
  TMergeRightsOwnersPayload,
  TMergeRightsOwnersResponse,
  TUpdateRightsOwnerPayload,
  TUpdateRightsOwnerResponse,
  TDisableTracksPayload,
  TDisableTracksResponse,
  TGetTrack,
  TGetTrackRightsOwnersPayload,
  TGetTrackRightsOwnersResponse,
  TSearchAdminRightsOwnersPayload,
  TSearchAdminRightsOwnersResponse,
  TListTracksPayload,
  TListTracksResponse,
  TMergeWritersRequest,
  TMergeWritersResponse,
  TUpdateWriterRequest,
  TDeleteWriterRequest,
  TSearchTrackArtistsResponse,
  TSearchTrackLibrariesResponse,
  TSearchTrackPartnersResponse,
  TSearchTracksPayload,
  TSearchTracksResponse,
  TSearchTracksTypes,
  TUpdateTrackRequest,
  TUpdateTrackResponse,
} from './types';

export type TSearchTracksFilters = {
  types: TSearchTracksTypes;
  filters: {
    titleTerm?: string;
    artistTerm?: string;
    libraryTerm?: string;
  };
};

export const tracksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrack: builder.query<TGetTrack, { id: number }>({
      query: (body) => ({
        url: 'tracks/tracks/get',
        method: 'POST',
        body,
      }),
      providesTags: (result, error, { id }) => [{ type: 'Track', id }],
    }),
    searchAdminRightsOwners: builder.query<TSearchAdminRightsOwnersResponse, TSearchAdminRightsOwnersPayload>({
      query: (payload) => {
        const cleanFilters = payload.filters
          ? Object.entries(payload.filters).reduce((acc, [key, value]) => {
              if (value !== '' && value !== null && value !== undefined) {
                acc[key] = value;
              }
              return acc;
            }, {} as any)
          : {};

        return {
          url: 'tracks/rights-owners/adminSearch',
          method: 'POST',
          body: {
            ...payload,
            filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : undefined,
          },
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ['rightsOwnersList'],
    }),
    getTrackRightsOwners: builder.query<TGetTrackRightsOwnersResponse, TGetTrackRightsOwnersPayload>({
      query: (body) => ({
        url: 'tracks/rights-owners/search',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
    }),
    searchTracks: builder.query<TSearchTracksResponse, TSearchTracksPayload>({
      query: (body) => ({
        url: 'tracks/tracks/search',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
    }),
    searchTracksArtists: builder.query<TSearchTrackArtistsResponse, { term: string }>({
      query: (body) => ({
        url: 'tracks/artists/search',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['tracksArtistsSearch'],
    }),
    searchTracksLibraries: builder.query<TSearchTrackLibrariesResponse, { term: string; partnerId?: number }>({
      query: (body) => ({
        url: 'tracks/libraries/search',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
    }),
    searchTracksPartners: builder.query<TSearchTrackPartnersResponse, { term: string; limit?: number }>({
      query: (body) => ({
        url: 'tracks/partners/search',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['tracksPartnersSearch'],
    }),
    createTrackArtist: builder.mutation<{ data: { id: number; name: string } }, { name: string }>({
      query: (body) => ({ url: 'tracks/artists/create', method: 'POST', body }),
      invalidatesTags: ['tracksArtistsSearch'],
    }),
    updateTrackArtist: builder.mutation<{ data: { id: number; name: string } }, { id: number; name: string }>({
      query: (body) => ({ url: 'tracks/artists/update', method: 'POST', body }),
      invalidatesTags: ['tracksArtistsSearch'],
    }),
    deleteTrackArtist: builder.mutation<void, { ids: number[] }>({
      query: (body) => ({ url: 'tracks/artists/delete', method: 'POST', body }),
      invalidatesTags: ['tracksArtistsSearch'],
    }),
    mergeTrackArtists: builder.mutation<{ id: number }, { ids: number[]; newName?: string }>({
      query: (body) => ({ url: 'tracks/artists/merge', method: 'POST', body }),
      invalidatesTags: ['tracksArtistsSearch'],
    }),
    getAllTrackArtists: builder.query<
      {
        data: Array<{ id: number; name: string }>;
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      },
      { searchTerm?: string; page?: number; pageSize?: number }
    >({
      query: (body) => ({
        url: 'tracks/artists/get-all',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['tracksArtistsSearch'],
    }),
    listTracks: builder.query<TListTracksResponse, TListTracksPayload>({
      query: (payload) => {
        // Clean up filters - remove empty values
        const cleanFilters = payload.filters ? Object.entries(payload.filters).reduce((acc, [key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any) : {};

        return {
          url: 'tracks/tracks/list',
          method: 'POST',
          body: {
            ...payload,
            filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : undefined,
          },
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ['tracksList'],
    }),
    getTenants: builder.query<{ data: Array<{ id: number; name: string }> }, void>({
      query: () => ({
        url: 'tracks/tracks/tenants',
        method: 'POST',
      }),
      keepUnusedDataFor: 300,
    }),
    getLibraries: builder.query<{ data: Array<{ id: number; name: string }> }, void>({
      query: () => ({
        url: 'tracks/tracks/libraries',
        method: 'POST',
      }),
      keepUnusedDataFor: 300,
    }),
    updateTrack: builder.mutation<TUpdateTrackResponse, TUpdateTrackRequest>({
      query: (body) => ({
        url: 'tracks/tracks/update-basic',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tracksList'],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Also invalidate the specific track query
          dispatch(tracksApi.util.invalidateTags([{ type: 'Track', id }]));
        } catch {
          // Error handling is done in the component
        }
      },
    }),
    createPartner: builder.mutation<
      { id: number; name: string; textKey: string; isCueSheetRecipient: boolean },
      { name: string; textKey: string; isCueSheetRecipient: boolean }
    >({
      query: (body) => ({ url: 'tracks/partners/create', method: 'POST', body }),
      invalidatesTags: ['tracksPartnersSearch'],
    }),
    editPartnerName: builder.mutation<{ data: { id: number; name: string } }, { id: number; name: string }>({
      query: (body) => ({ url: 'tracks/partners/edit-partner-name', method: 'POST', body }),
      invalidatesTags: ['tracksPartnersSearch'],
    }),
    deletePartner: builder.mutation<void, { id: number }>({
      query: (body) => ({ url: 'tracks/partners/delete-partner', method: 'POST', body }),
      invalidatesTags: ['tracksPartnersSearch'],
    }),
    disableTracks: builder.mutation<TDisableTracksResponse, TDisableTracksPayload>({
      query: (body) => ({
        url: 'tracks/tracks/disable',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tracksList'],
    }),
    mergeTracks: builder.mutation<
      { referenceId: number; mergedCount: number; updatedQClearTracksCount: number },
      { ids: number[]; referenceId: number }
    >({
      query: (body) => ({
        url: 'tracks/tracks/merge',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tracksList'],
    }),
    mergeWriters: builder.mutation<TMergeWritersResponse, TMergeWritersRequest>({
      query: (body) => ({
        url: 'tracks/rights-owners/merge-writers',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rightsOwnersList'],
    }),
    updateWriter: builder.mutation<void, TUpdateWriterRequest>({
      query: (body) => ({
        url: 'tracks/rights-owners/update-writer',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rightsOwnersList'],
    }),
    deleteWriter: builder.mutation<void, TDeleteWriterRequest>({
      query: (body) => ({
        url: 'tracks/rights-owners/delete-writer',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rightsOwnersList'],
    }),
    bulkUpdateLibraryDetails: builder.mutation<
      { updatedCount: number },
      {
        trackIds: number[];
        partnerId?: number | null;
        setAsVerified?: boolean;
        oneStopCombined?: boolean;
        syncLicensorId?: number | null;
      }
    >({
      query: (body) => ({
        url: 'tracks/tracks/bulk-update-library-details',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tracksList'],
      async onQueryStarted({ trackIds }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            tracksApi.util.invalidateTags(
              trackIds.map((id) => ({ type: 'Track' as const, id })),
            ),
          );
        } catch {
          // Error handling is done in the component
        }
      },
    }),
    disableRightsOwners: builder.mutation<TDisableRightsOwnersResponse, TDisableRightsOwnersPayload>({
      query: (body) => ({
        url: 'tracks/rights-owners/disable',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rightsOwnersList'],
    }),
    updateRightsOwner: builder.mutation<TUpdateRightsOwnerResponse, TUpdateRightsOwnerPayload>({
      query: (body) => ({
        url: 'tracks/rights-owners/update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rightsOwnersList'],
    }),
    mergeRightsOwners: builder.mutation<TMergeRightsOwnersResponse, TMergeRightsOwnersPayload>({
      query: (body) => ({
        url: 'tracks/rights-owners/merge',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rightsOwnersList'],
    }),
    searchPros: builder.query<{ content: Array<{ id: number; name: string }>; total: number }, {
      term: string;
      limit?: number;
    }>({
      query: ({ term, limit }) => ({
        url: 'tracks/pros/get-all',
        method: 'POST',
        body: { searchTerm: term || undefined, pageSize: limit },
      }),
      keepUnusedDataFor: 0,
      providesTags: ['prosList'],
    }),
    createPro: builder.mutation<{ id: number; name: string }, { name: string }>({
      query: (body) => ({ url: 'tracks/pros/create', method: 'POST', body }),
      invalidatesTags: ['prosList'],
    }),
    deletePro: builder.mutation<void, { ids: number[] }>({
      query: (body) => ({ url: 'tracks/pros/delete', method: 'POST', body }),
      invalidatesTags: ['prosList'],
    }),
  }),
});

export const {
  useGetTrackQuery,
  useSearchAdminRightsOwnersQuery,
  useGetTrackRightsOwnersQuery,
  useMergeWritersMutation,
  useUpdateWriterMutation,
  useDeleteWriterMutation,
  useSearchTracksQuery,
  useSearchTracksArtistsQuery,
  useCreateTrackArtistMutation,
  useUpdateTrackArtistMutation,
  useDeleteTrackArtistMutation,
  useMergeTrackArtistsMutation,
  useGetAllTrackArtistsQuery,
  useSearchTracksLibrariesQuery,
  useSearchTracksPartnersQuery,
  useCreatePartnerMutation,
  useEditPartnerNameMutation,
  useDeletePartnerMutation,
  useListTracksQuery,
  useGetTenantsQuery,
  useGetLibrariesQuery,
  useUpdateTrackMutation,
  useDisableTracksMutation,
  useMergeTracksMutation,
  useBulkUpdateLibraryDetailsMutation,
  useDisableRightsOwnersMutation,
  useMergeRightsOwnersMutation,
  useUpdateRightsOwnerMutation,
  useSearchProsQuery,
  useCreateProMutation,
  useDeleteProMutation,
} = tracksApi;
